import { Injectable } from '@angular/core';
import { Api } from '../shared/api';
import { Globals } from '../shared/globals';
import { JsonSerializer } from 'typescript-json-serializer';
import { LmStationGroup } from '../models/lm-station-group.model';
import { LmBuildingGroup } from '../models/lm-building-group.model';
import { LmStation } from '../models/lm-station.model';
import { SmartmeMeter } from '../models/smartme-meter.model';

@Injectable()
export class LoadmanagementService {
  private jsonSerializer = new JsonSerializer();

  lmStationGroupsUrl: string;
  lmStationGroupUrl = (stationGroupId: string): string =>
    `${this.lmStationGroupsUrl}/${stationGroupId}`;
  lmStationGroupStationsUrl = (stationGroupId: string): string =>
    `${this.lmStationGroupsUrl}/${stationGroupId}/stations`;
  lmStationGroupStationUrl = (
    stationGroupId: string,
    stationId: string
  ): string => `${this.lmStationGroupStationsUrl(stationGroupId)}/${stationId}`;

  lmBuildingGroupsUrl: string;
  lmBuildingGroupUrl = (buildingGroupId: string): string =>
    `${this.lmBuildingGroupsUrl}/${buildingGroupId}`;
  lmBuildingGroupStationGroupsUrl = (buildingGroupId: string): string =>
    `${this.lmBuildingGroupsUrl}/${buildingGroupId}/station_groups`;
  lmBuildingGroupStationGroupUrl = (
    buildingGroupId: string,
    stationGroupId: string
  ): string =>
    `${this.lmBuildingGroupStationGroupsUrl(
      buildingGroupId
    )}/${stationGroupId}`;

  lmUnassignedStationsUrl: string;

  smartmeMetersUrl: string;

  unassignedLmStationGroupsUrl: string;

  constructor(private globals: Globals, private api: Api) {
    this.lmStationGroupsUrl = this.globals.apiUrl(
      'loadmanagement/station_groups'
    );
    this.lmBuildingGroupsUrl = this.globals.apiUrl(
      'loadmanagement/building_groups'
    );
    this.lmUnassignedStationsUrl = this.globals.apiUrl(
      'loadmanagement/stations'
    );
    this.smartmeMetersUrl = this.globals.apiUrl(
      'loadmanagement/building_groups/meters'
    );
    this.unassignedLmStationGroupsUrl = this.globals.apiUrl(
      'loadmanagement/building_groups/station_groups'
    );
  }

  public async getLmUnassignedStations(): Promise<Array<LmStation>> {
    const lmUnassignedStations: string = await this.api.httpGet(
      this.lmUnassignedStationsUrl
    );
    return this.jsonSerializer.deserializeObjectArray(
      lmUnassignedStations,
      LmStation
    );
  }

  public async getLmStationGroups(): Promise<Array<LmStationGroup>> {
    const lmStationGroups: string = await this.api.httpGet(
      this.lmStationGroupsUrl
    );
    return this.jsonSerializer.deserializeObjectArray(
      lmStationGroups,
      LmStationGroup
    );
  }

  public async addLmStationGroup(): Promise<string> {
    return await this.api.httpPost(this.lmStationGroupsUrl, {});
  }

  public async updateLmStationGroup(
    stationGroup: LmStationGroup
  ): Promise<void> {
    return await this.api.httpPatch(
      this.lmStationGroupUrl(stationGroup.id),
      stationGroup
    );
  }

  public async deleteLmStationGroup(stationGroupId: string): Promise<void> {
    return await this.api.httpDelete(this.lmStationGroupUrl(stationGroupId));
  }

  public async getLmStationGroupStations(
    stationGroupId: string
  ): Promise<Array<LmStation>> {
    const lmStations: string = await this.api.httpGet(
      this.lmStationGroupStationsUrl(stationGroupId)
    );
    return this.jsonSerializer.deserializeObjectArray(lmStations, LmStation);
  }

  public async addLmStationGroupStation(
    stationGroupId: string,
    station: LmStation
  ): Promise<void> {
    return await this.api.httpPost(
      this.lmStationGroupStationsUrl(stationGroupId),
      station
    );
  }

  public async updateLmStationGroupStation(
    stationGroupId: string,
    station: LmStation
  ): Promise<void> {
    return await this.api.httpPatch(
      this.lmStationGroupStationUrl(stationGroupId, station.id),
      station
    );
  }

  public async removeLmStationGroupStation(
    stationGroupId: string,
    stationId: string
  ): Promise<void> {
    return await this.api.httpDelete(
      this.lmStationGroupStationUrl(stationGroupId, stationId)
    );
  }

  public async getLmBuildingGroups(): Promise<Array<LmBuildingGroup>> {
    const lmBuildingGroups: string = await this.api.httpGet(
      this.lmBuildingGroupsUrl
    );
    return this.jsonSerializer.deserializeObjectArray(
      lmBuildingGroups,
      LmBuildingGroup
    );
  }

  public async addLmBuildingGroup(): Promise<string> {
    return await this.api.httpPost(this.lmBuildingGroupsUrl, {});
  }

  public async updateLmBuildingGroup(
    buildingGroup: LmBuildingGroup
  ): Promise<void> {
    return await this.api.httpPatch(
      this.lmBuildingGroupUrl(buildingGroup.id),
      buildingGroup
    );
  }

  public async deleteLmBuildingGroup(buildingGroupId: string): Promise<void> {
    return await this.api.httpDelete(this.lmBuildingGroupUrl(buildingGroupId));
  }

  public async getLmBuildingGroupStationGroups(
    buildingGroupId: string
  ): Promise<Array<LmStationGroup>> {
    const lmStations: string = await this.api.httpGet(
      this.lmBuildingGroupStationGroupsUrl(buildingGroupId)
    );
    return this.jsonSerializer.deserializeObjectArray(
      lmStations,
      LmStationGroup
    );
  }

  public async addLmBuildingGroupStationGroup(
    buildingGroupId: string,
    stationGroupId: string
  ): Promise<void> {
    return await this.api.httpPost(
      this.lmBuildingGroupStationGroupsUrl(buildingGroupId),
      {
        id: stationGroupId,
      }
    );
  }

  public async removeLmBuildingGroupStationGroup(
    buildingGroupId: string,
    stationGroupId: string
  ): Promise<void> {
    return await this.api.httpDelete(
      this.lmBuildingGroupStationGroupUrl(buildingGroupId, stationGroupId)
    );
  }

  public async getSmartmeMeters(): Promise<Array<SmartmeMeter>> {
    const response: string = await this.api.httpGet(this.smartmeMetersUrl);
    return this.jsonSerializer.deserializeObjectArray(response, SmartmeMeter);
  }

  public async getUnassignedLmStationGroups(): Promise<Array<LmStationGroup>> {
    const response: string = await this.api.httpGet(
      this.unassignedLmStationGroupsUrl
    );
    return this.jsonSerializer.deserializeObjectArray(response, LmStationGroup);
  }
}
